import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** What a suburb page can be missing (conductor-server suburb-readiness.service.ts MISSING_KEYS). */
export type ReadinessGap =
  | 'published'
  | 'opening'
  | 'about'
  | 'disposal'
  | 'serviceVenues'
  | 'wakeVenues'
  | 'families'
  | 'arrangers'
  | 'faqs'
  | 'gate'
  | 'owner';

export type ReadinessStatus = 'todo' | 'in-progress' | 'done';

export interface ReadinessPerson {
  userId: number;
  name: string;
}

/** One suburb, place or sub-region page in the readiness view (#262). */
export interface ReadinessRow {
  recordId: string;
  kind: 'suburb' | 'place' | 'subregion' | null;
  template: 'suburb' | 'subregion';
  name: string;
  slug: string;
  regionId: string;
  regionSlug: string;
  salCode: string | null;
  url: string;
  subRegionIds: string[];
  published: boolean;
  lastPublishedAt: string | null;
  lastPublishedBy: ReadinessPerson | null;
  opening: { state: 'written' | 'draft' | 'empty'; draftsWaiting: number; dataLead: string | null };
  about: boolean;
  places: {
    crematoria: number;
    cemeteries: number;
    serviceVenues: number;
    wakeVenues: number;
  } | null;
  families: string | null;
  arrangers: { shown: ReadinessPerson[]; unpublished: number };
  faqs: number;
  /** Not built yet (#252). */
  photos: null;
  insertedBlocks: number;
  gate: {
    pass: boolean;
    g1: boolean;
    g2: boolean;
    g3: boolean;
    g4: boolean;
    reportedAt: string;
  } | null;
  owner: ReadinessPerson | null;
  status: ReadinessStatus;
  missing: ReadinessGap[];
}

export interface ReadinessQuery {
  region?: string | null;
  /** A sub-region id, or 'none'. */
  subRegion?: string | null;
  /** A user id, or 'none'. */
  owner?: string | null;
  status?: ReadinessStatus | null;
  /** Rows missing ANY of these. */
  missing?: ReadinessGap[] | null;
  published?: 'yes' | 'no' | null;
  /** The template: suburb (suburbs, places) or subregion. */
  kind?: 'suburb' | 'subregion' | null;
  q?: string | null;
  sort?: 'name' | 'region' | 'status' | 'owner' | 'gaps';
  dir?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}

export interface ReadinessPage {
  total: number;
  offset: number;
  limit: number;
  rows: ReadinessRow[];
  meta: {
    gateReportedAt: string | null;
    arrangersComputedAt: string | null;
    locationsSnapshotId: string | null;
    templates: Record<
      'suburb' | 'subregion',
      { lastPublishedAt: string | null; publishedPages: number }
    >;
  };
}

/** One block of a page as the site builds it: a template block (shown or hidden, and why) or an inserted one. */
export interface ReadinessBlock {
  id: string;
  source: 'template' | 'inserted';
  after?: string;
  type: 'editorial-hero' | 'section';
  component: string | null;
  /** With the page's placeholders filled in. */
  heading: string;
  shows: boolean;
  reason: string;
}

export interface ReadinessDetail extends ReadinessRow {
  blocks: ReadinessBlock[];
  faqIds: string[];
  /** Template blocks that were removed (their tombstones). */
  templateRemoved: string[];
}

export interface ConvertPage {
  pageId: string;
  path: string;
  recordId: string;
  sections: Array<{
    id: string;
    heading?: string;
    outcome: string;
    as?: string;
    after?: string;
    note?: string;
  }>;
  fieldsSet: unknown;
  insertionIds: string[];
  blocked?: string;
  scope: string;
  applied?: boolean;
}

/**
 * The suburb page readiness view and the page templates' Developer actions (conductor-server #262): one row per
 * suburb, place and sub-region page with what it has and what's missing, its owner and status; one page's blocks; the
 * templates' seed; and converting a page designed before the template.
 */
@Injectable({ providedIn: 'root' })
export class SuburbReadinessService {
  private readonly paths = environment.api.paths.api.suburbReadiness;

  constructor(private http: HttpClient) {}

  list(q: ReadinessQuery): Observable<ReadinessPage> {
    return this.http
      .get<any>(this.url(this.paths.list), { params: readinessParams(q) })
      .pipe(map((r) => r.data));
  }

  owners(): Observable<ReadinessPerson[]> {
    return this.http.get<any>(this.url(this.paths.owners)).pipe(map((r) => r.data));
  }

  detail(recordId: string): Observable<ReadinessDetail> {
    return this.http.get<any>(this.oneUrl(recordId)).pipe(map((r) => r.data));
  }

  update(
    recordId: string,
    change: { ownerUserId?: number | null; status?: ReadinessStatus }
  ): Observable<ReadinessRow> {
    return this.http.patch<any>(this.oneUrl(recordId), change).pipe(map((r) => r.data));
  }

  /** Developer: write whichever default template is missing (never overwrites). */
  seedTemplates(): Observable<{ created: string[]; existing: string[] }> {
    return this.http.post<any>(this.url(this.paths.seedTemplates), {}).pipe(map((r) => r.data));
  }

  /** Developer: convert the designed page of these records to the template plus inserted blocks. */
  convert(
    recordIds: string[],
    dryRun: boolean
  ): Observable<{ pages: ConvertPage[]; totals: Record<string, number> }> {
    return this.http
      .post<any>(this.url(this.paths.convert), { recordIds, dryRun })
      .pipe(map((r) => r.data));
  }

  private url(path: string): string {
    return environment.api.host + path;
  }

  private oneUrl(recordId: string): string {
    return this.url(this.paths.one.replace(':recordId', encodeURIComponent(recordId)));
  }
}

/** The list's query string: empty values left out, `missing` as a comma list. */
export function readinessParams(q: ReadinessQuery): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
    } else out[k] = String(v);
  }
  return out;
}
